import React from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import "./styles/main.scss";

//Layouts
import AdminLayout from "./layouts/admin";
import MainLayout from "./layouts/main";
import EmptyLayout from "./layouts/empty";
import PreshipmentLayout from "./layouts/preshipment";
import CodingLayout from "./layouts/coding";
import PhotoLayout from "./layouts/photo";
import DuplicateStageLayout from "layouts/duplicate-stage";

import AdminRoutes from "./admin/routes";
import PreshipmentRoutes from "./admin-preshipment/routes";

import BinLabelIndex from "./components/bin-label/index";
import BinMoverIndex from "./components/bin-mover/index";
import BrandVerifiedQAIndex from "components/brand-verified-qa";
import CodingIndex from "./components/coding";
import DuplicateStagingIndex from "components/duplicate-staging";
import FinalQAIndex from "./components/final-qa/index";
import HomeIndex from "./components/home/index";
import ImageBrowserIndex from "./components/image-browser/index";
import ImageEditingIndex from "./components/image-editing/index";
import MaintenanceIndex from "./components/maintenance/index";
import PhotographyIndex from "./components/photography/index";
import ProductMatchGallery from "./components/image-browser/product-match-gallery";
import ProductCompareIndex from "components/duplicate-staging/product-compare";
import SchedulingIndex from "./components/scheduling/index";
import SearchToolIndex from "./components/search-tool/index";
import StyleguideIndex from "./components/styleguide/index";
import ProfilingListIndex from "./components/scheduling-list/index";
import ShipmentEditorIndex from "./components/shipment-editor/index";
import ShipmentsIndex from "./components/shipments/index";
import TaskListIndex from "./components/task-list/index";
import Callback from "./components/callback/callback";
import Logout from "./components/logout";

import { TaskListSettingsProvider } from "./contexts/task-list-settings/task-list-settings";
import Auth from "./contexts/auth/auth";
import { DictionaryProvider } from "./contexts/dictionary/dictionary-provider";
import SettingsProvider from "contexts/settings/settings";

import ShipmentStatus from "./components/shipment-status";
import { PrivateRoute } from "./shared/private-route";

const Home = (props) => {
    return (
        <MainLayout>
            <HomeIndex {...props} />
        </MainLayout>
    );
};

const withLayout = (LayoutComp, ComponentComp, props) => {
    return (
        <LayoutComp>
            <ComponentComp {...props} />
        </LayoutComp>
    );
};
const withDefaultLayout = (ComponentComp) => (props) => withLayout(MainLayout, ComponentComp, props);
const withCodingLayout = (ComponentComp) => (props) => withLayout(CodingLayout, ComponentComp, props);
const withPhotoLayout = (ComponentComp) => (props) => withLayout(PhotoLayout, ComponentComp, props);
const withEmptyLayout = (ComponentComp) => (props) => withLayout(EmptyLayout, ComponentComp, props);
const withAdminLayout = (ComponentComp) => (props) => withLayout(AdminLayout, ComponentComp, props);
const withPreshipmentLayout = (ComponentComp) => (props) => withLayout(PreshipmentLayout, ComponentComp, props);
const withDuplicateStageLayout = (ComponentComp) => (props) => withLayout(DuplicateStageLayout, ComponentComp, props);

const AdminPage = withAdminLayout(AdminRoutes);
const PreshipmentPage = withPreshipmentLayout(PreshipmentRoutes);
const ShipmentPage = withDefaultLayout(ShipmentsIndex);
const ProfilingPage = withDefaultLayout(ProfilingListIndex);
const SchedulingPage = withDefaultLayout(SchedulingIndex);
const ShipmentEditorPage = withDefaultLayout(ShipmentEditorIndex);
const ShipmentStatusPage = withDefaultLayout(ShipmentStatus);
const BinLabelPage = withDefaultLayout(BinLabelIndex);
const DuplicateStagingPage = withDefaultLayout(DuplicateStagingIndex);
const ProductComparePage = withDuplicateStageLayout(ProductCompareIndex);
const PhotographyPage = withPhotoLayout(PhotographyIndex);
const ImageEditingPage = withPhotoLayout(ImageEditingIndex);
const CodingPage = withCodingLayout(CodingIndex);
const ProductMatchImageBrowserPage = withEmptyLayout(ProductMatchGallery);
const ImageBrowserPage = withEmptyLayout(ImageBrowserIndex);
const StyleGuidePage = withEmptyLayout(StyleguideIndex);
const MaintenancePage = withEmptyLayout(MaintenanceIndex);
const SearchToolPage = withDefaultLayout(SearchToolIndex);
const BinMoverPage = withDefaultLayout(BinMoverIndex);
const FinalQaPage = withCodingLayout(FinalQAIndex);
const BrandVerifiedQaPage = withCodingLayout(BrandVerifiedQAIndex);

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
        },
    },
});

const App = () => {
    return (
        <div className="App">
            <QueryClientProvider client={queryClient}>
                <BrowserRouter>
                    <DictionaryProvider>
                        <Auth>
                            <TaskListSettingsProvider>
                                <SettingsProvider>
                                    <Routes>
                                        <Route
                                            exact
                                            path="/"
                                            element={<Home />}
                                        />
                                        <Route
                                            path="/callback"
                                            element={<Callback />}
                                        />
                                        <Route
                                            path="/logout"
                                            element={<Logout />}
                                        />
                                        <Route
                                            path="/admin/*"
                                            element={
                                                <PrivateRoute
                                                    path="/admin"
                                                    component={AdminPage}
                                                    checkAllowedRoutes={true}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/preshipment/*"
                                            element={
                                                <PrivateRoute
                                                    path="/preshipment"
                                                    component={PreshipmentPage}
                                                    checkAllowedRoutes={true}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/shipments"
                                            element={
                                                <PrivateRoute
                                                    path="/shipments"
                                                    component={ShipmentPage}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/duplicate-staging"
                                            element={
                                                <PrivateRoute
                                                    path="/duplicate-staging"
                                                    component={DuplicateStagingPage}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/duplicate-staging/:id?"
                                            element={
                                                <PrivateRoute
                                                    path="/duplicate-staging/:id?"
                                                    component={ProductComparePage}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/profiling"
                                            element={
                                                <PrivateRoute
                                                    exact
                                                    path="/profiling"
                                                    component={ProfilingPage}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/profiling/:shipmentKey/:shipmentType?"
                                            element={
                                                <PrivateRoute
                                                    path="/profiling/:shipmentKey/:shipmentType?"
                                                    component={SchedulingPage}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/scheduling"
                                            element={
                                                <PrivateRoute
                                                    exact
                                                    path="/scheduling"
                                                    component={ProfilingPage}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/scheduling/:shipmentKey/:shipmentType?"
                                            element={
                                                <PrivateRoute
                                                    path="/scheduling/:shipmentKey/:shipmentType?"
                                                    component={SchedulingPage}
                                                />
                                            }
                                        />

                                        <Route
                                            path="/task-list/:type?"
                                            element={
                                                <PrivateRoute
                                                    path="/task-list/:type?"
                                                    component={withDefaultLayout(TaskListIndex)}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/shipment-editor/:id?"
                                            element={
                                                <PrivateRoute
                                                    path="/shipment-editor/:id?"
                                                    component={ShipmentEditorPage}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/shipment-status/:shipmentKey"
                                            element={
                                                <PrivateRoute
                                                    path="/shipment-status/:shipmentKey"
                                                    component={ShipmentStatusPage}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/bin-label/:id/:delay?"
                                            element={
                                                <PrivateRoute
                                                    path="/bin-label/:id/:delay?"
                                                    component={BinLabelPage}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/photography/:id/:wip"
                                            element={
                                                <PrivateRoute
                                                    path="/photography/:id/:wip"
                                                    component={PhotographyPage}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/image-editing/:id/:wip"
                                            element={
                                                <PrivateRoute
                                                    path="/image-editing/:id/:wip"
                                                    component={ImageEditingPage}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/image-editing-qa/:id/:wip"
                                            element={
                                                <PrivateRoute
                                                    path="/image-editing-qa/:id/:wip"
                                                    component={ImageEditingPage}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/coding/:id/:wip/:group?"
                                            element={
                                                <PrivateRoute
                                                    path="/coding/:id/:wip/:group?"
                                                    component={CodingPage}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/coding-qa/:id/:wip/:group?"
                                            element={
                                                <PrivateRoute
                                                    path="/coding-qa/:id/:wip/:group?"
                                                    component={CodingPage}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/final-qa/:id/:wip"
                                            element={
                                                <PrivateRoute
                                                    path="/final-qa/:id/:wip"
                                                    component={FinalQaPage}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/brand-verified-qa/:id/:wip"
                                            element={
                                                <PrivateRoute
                                                    path="/brand-verified-qa/:id/:wip"
                                                    component={BrandVerifiedQaPage}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/tools/bin-mover"
                                            element={
                                                <PrivateRoute
                                                    path="/tools/bin-mover"
                                                    component={BinMoverPage}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/tools/search"
                                            element={
                                                <PrivateRoute
                                                    path="/tools/search"
                                                    component={SearchToolPage}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/styleguide"
                                            element={<StyleGuidePage />}
                                        />
                                        <Route
                                            path="/maintenance"
                                            element={<MaintenancePage />}
                                        />
                                        <Route
                                            path="/image-browser/product-match/:upc/:codingWipKey/:id"
                                            element={
                                                <PrivateRoute
                                                    path="/image-browser/product-match/:upc/:codingWipKey/:id"
                                                    component={ProductMatchImageBrowserPage}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/image-browser/:shipmentKey/:shipmentProductKey?/:version?"
                                            element={
                                                <PrivateRoute
                                                    path="/image-browser/:shipmentKey/:shipmentProductKey?/:version?"
                                                    component={ImageBrowserPage}
                                                />
                                            }
                                        />
                                    </Routes>
                                </SettingsProvider>
                            </TaskListSettingsProvider>
                        </Auth>
                    </DictionaryProvider>
                </BrowserRouter>
            </QueryClientProvider>
        </div>
    );
};

export default App;
